import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion, type Variants } from "framer-motion";
import shortid from "shortid";
import {
  ArrowRight,
  CheckCircle2,
  Eye,
  EyeOff,
  Layers,
  Lock,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";
import { signupFree } from "../../../api/General/PlatformTerms";
import { getCountries } from "../../../api/General/Country";
import { track, AnalyticsEvents } from "../../../utils/analytics";
import { CountryView, Error as ApiError } from "../../../models/general";
import { validateEmail } from "../../../utils";
import { cn } from "../../../utils/cn";

import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import { Checkbox } from "../../../components/ui/Checkbox";
import TermsDialog from "../../../components/terms/TermsDialog";

import Logo from "../../../assets/images/logo.png";
import CircleCheck from "../../../assets/images/circleCheck.png";

/**
 * SignupFree — public, anonymous on-ramp that creates a Free-tier company.
 *
 * Brand surface (per PRODUCT.md register=brand): cobalt + turquoise mesh,
 * inline brand mark + wordmark, gradient-border form card, Framer-Motion
 * staggered entrance, ambient sheen + progress shimmer. All ambient motion
 * collapses under prefers-reduced-motion (see src/index.css `.mz-*` rules).
 */

function generatePassword(): string {
  return `${shortid.generate()}@Abc123`;
}

interface FormState {
  companyName: string;
  firstName: string;
  lastName: string;
  email: string;
  countryId: number | "";
  phoneNumber: string;
  password: string;
}

/** Server-rendered Razor login page. */
const LOGIN_URL = "/Account/Login";

const EASE = [0.22, 1, 0.36, 1] as const;

const SignupFree: React.FC = () => {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();

  const goToLogin = () => {
    if (typeof window !== "undefined") window.location.assign(LOGIN_URL);
  };

  const [form, setForm] = useState<FormState>(() => ({
    companyName: "",
    firstName: "",
    lastName: "",
    email: "",
    countryId: "",
    phoneNumber: "",
    password: generatePassword(),
  }));
  const [countries, setCountries] = useState<CountryView[]>([]);
  const [accepted, setAccepted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [touched, setTouched] = useState(false);

  const timeZone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch {
      return "UTC";
    }
  }, []);

  const referenceSlug = useMemo(() => {
    if (typeof window === "undefined") return null;
    const raw = new URLSearchParams(window.location.search).get("ref");
    if (!raw) return null;
    const trimmed = raw.trim();
    return trimmed.length === 0 ? null : trimmed.slice(0, 64);
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await getCountries();
        if (!alive) return;
        if (!res.errors || res.errors.length === 0) setCountries(res.payload || []);
      } catch {
        /* surface inline only on submit */
      }
    })();
    return () => { alive = false; };
  }, []);

  const redirectTimer = useRef<number | null>(null);
  useEffect(() => {
    if (submitted) {
      redirectTimer.current = window.setTimeout(() => goToLogin(), 5000);
    }
    return () => {
      if (redirectTimer.current) window.clearTimeout(redirectTimer.current);
    };
  }, [submitted]);

  const fieldErrors = useMemo(() => {
    const e: Partial<Record<keyof FormState | "terms", string>> = {};
    if (!form.companyName.trim()) e.companyName = t("Required");
    if (!form.firstName.trim()) e.firstName = t("Required");
    if (!form.lastName.trim()) e.lastName = t("Required");
    if (!form.email.trim()) e.email = t("Required");
    else if (!validateEmail(form.email)) e.email = t("Please enter a valid email.");
    if (!form.countryId) e.countryId = t("Required");
    if (!form.password || form.password.length < 8) e.password = t("Password is too short.");
    if (!accepted) e.terms = t("Please accept the Terms of Service to continue.");
    return e;
  }, [form, accepted, t]);

  const isValid = Object.keys(fieldErrors).length === 0;

  const progress = useMemo(() => {
    const filled = [
      form.companyName.trim().length > 0,
      form.firstName.trim().length > 0,
      form.lastName.trim().length > 0,
      validateEmail(form.email),
      !!form.countryId,
      form.password.length >= 8,
      accepted,
    ].filter(Boolean).length;
    return Math.round((filled / 7) * 100);
  }, [form, accepted]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const submit = async () => {
    setTouched(true);
    setServerError(null);
    if (!isValid) return;
    setSubmitting(true);
    try {
      const res = await signupFree({
        company: {
          name: form.companyName.trim(),
          countryId: Number(form.countryId),
          phoneNumber: form.phoneNumber.trim(),
          timeZone,
        },
        user: {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          phoneNumber: form.phoneNumber.trim(),
          password: form.password,
          countryId: Number(form.countryId),
        },
        acceptedTerms: true,
        referenceSlug,
      });
      if (res.errors && res.errors.length > 0) {
        const err: ApiError = res.errors[0];
        setServerError(err.description || err.message || t("Something went wrong. Please try again."));
        setSubmitting(false);
        return;
      }
      track(AnalyticsEvents.SignupFreeSubmitted, {
        has_reference: !!referenceSlug,
        reference: referenceSlug || null,
      });
      setSubmitted(true);
    } catch (e: unknown) {
      const apiErr =
        (e as { errors?: ApiError[] })?.errors?.[0] ||
        (e as { response?: { data?: { errors?: ApiError[] } } })?.response?.data?.errors?.[0];
      setServerError(
        apiErr?.description || apiErr?.message || t("Something went wrong. Please try again."),
      );
      setSubmitting(false);
    }
  };

  /* ─────────────────────── Success state ─────────────────────── */
  if (submitted) {
    return (
      <div className="min-h-screen relative flex items-center justify-center bg-[#F5F8FC] px-4 py-10 overflow-hidden">
        <BrandMesh />
        <motion.div
          initial={{ opacity: 0, y: 8, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.5, ease: EASE }}
          className="relative z-10 max-w-md w-full text-center"
        >
          <img src={CircleCheck} alt="" className="mx-auto h-20 w-20 mb-5" />
          <h1 className="text-2xl font-semibold text-foreground tracking-tight">
            {t("Welcome to Mowazaf.")}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {t("Your workspace is being set up. We've emailed you the next steps.")}
          </p>
          <div className="mt-6 flex items-center justify-center gap-3">
            <Button onClick={goToLogin}>
              {t("Continue to sign in")}
              <ArrowRight size={16} className="ms-2 rtl:rotate-180" />
            </Button>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            {t("Redirecting in a few seconds...")}
          </p>
        </motion.div>
      </div>
    );
  }

  /* ─────────────────────── Form state ─────────────────────── */

  const headlineParent: Variants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: reduceMotion ? 0 : 0.09,
        delayChildren: reduceMotion ? 0 : 0.18,
      },
    },
  };
  const lineVariants: Variants = {
    hidden: { opacity: 0, y: reduceMotion ? 0 : 8 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.52, ease: EASE } },
  };
  const featureParent: Variants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: reduceMotion ? 0 : 0.08,
        delayChildren: reduceMotion ? 0 : 0.5,
      },
    },
  };
  const featureItem: Variants = {
    hidden: { opacity: 0, y: reduceMotion ? 0 : 6 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.42, ease: EASE } },
  };
  const cardVariants: Variants = {
    hidden: { opacity: 0, x: reduceMotion ? 0 : 12 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.56, delay: reduceMotion ? 0 : 0.22, ease: EASE } },
  };
  const tailVariants: Variants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.6, delay: reduceMotion ? 0 : 0.85, ease: EASE } },
  };

  return (
    <div className="min-h-screen relative overflow-x-hidden bg-[#F5F8FC] text-foreground">
      <BrandMesh />

      <TermsDialog
        open={termsOpen}
        onOpenChange={setTermsOpen}
        kind="GeneralUsage"
        autoAccept={false}
        onAccepted={() => setAccepted(true)}
      />

      <div className="relative z-10 grid grid-cols-1 md:grid-cols-[44%_56%] lg:grid-cols-[40%_60%] min-h-screen">
        {/* ─── Left brand pane (desktop only) ─── */}
        <aside
          aria-hidden="true"
          className="hidden md:grid grid-rows-[auto_1fr_auto] p-10 lg:p-14 relative"
        >
          {/* Row 1 — brand block */}
          <motion.header
            initial={{ opacity: 0, y: reduceMotion ? 0 : 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.48, ease: EASE }}
            className="flex items-center gap-3"
          >
            <a
              href="/"
              className="group inline-flex items-center gap-3"
              aria-label="Mowazaf"
            >
              <img
                src={Logo}
                alt=""
                className="h-9 lg:h-10 w-auto select-none transition-transform duration-300 ease-out group-hover:scale-[1.04]"
              />
              <span className="text-lg lg:text-xl font-semibold tracking-tight text-foreground">
                {t("Mowazaf")}
              </span>
            </a>
          </motion.header>

          {/* Row 2 — headline + sub-copy + features (vertically centered) */}
          <div className="self-center max-w-[480px] py-12">
            <motion.h1
              variants={headlineParent}
              initial="hidden"
              animate="visible"
              className="text-4xl lg:text-5xl xl:text-[3.4rem] font-semibold tracking-tight leading-[1.05] text-foreground"
            >
              <motion.span variants={lineVariants} className="block">
                {t("Start free.")}
              </motion.span>
              <motion.span variants={lineVariants} className="block text-foreground/75">
                {t("Upgrade when you're ready.")}
              </motion.span>
            </motion.h1>

            <motion.p
              variants={lineVariants}
              initial="hidden"
              animate="visible"
              transition={{ duration: 0.5, delay: reduceMotion ? 0 : 0.42, ease: EASE }}
              className="mt-5 text-sm lg:text-base leading-relaxed text-muted-foreground max-w-[44ch]"
            >
              {t("Bring your team into a calm, modern HR workspace. No credit card to begin, no time pressure to upgrade.")}
            </motion.p>

            <motion.ul
              variants={featureParent}
              initial="hidden"
              animate="visible"
              className="mt-9 space-y-4"
            >
              <FeatureLine
                variants={featureItem}
                icon={<Users size={16} />}
                title={t("Built for the whole team")}
                body={t("Employees, managers, and HR working from one place.")}
              />
              <FeatureLine
                variants={featureItem}
                icon={<Layers size={16} />}
                title={t("Every module included")}
                body={t("Attendance, leaves, payroll, performance. Toggle what you need.")}
              />
              <FeatureLine
                variants={featureItem}
                icon={<ShieldCheck size={16} />}
                title={t("Secure by default")}
                body={t("Encrypted in transit and at rest, with role-based access so the right people see the right things.")}
              />
            </motion.ul>
          </div>

          {/* Row 3 — social proof */}
          <motion.div
            variants={tailVariants}
            initial="hidden"
            animate="visible"
          >
            <p className="text-xs text-muted-foreground/85 leading-snug max-w-[28ch]">
              {t("Trusted by HR teams all over the Region.")}
            </p>
          </motion.div>
        </aside>

        {/* ─── Right form pane ─── */}
        <main className="relative bg-white/80 md:bg-white/85 flex items-center justify-center p-4 sm:p-6 md:p-10">
          {/* Subtle inner left edge fade so the brand mesh doesn't pile up on the seam */}
          <div
            aria-hidden
            className="hidden md:block absolute top-0 bottom-0 ltr:left-0 rtl:right-0 w-12
                       bg-gradient-to-r rtl:bg-gradient-to-l from-white/0 via-white/40 to-white/70
                       pointer-events-none"
          />

          <motion.div
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            className="relative w-full max-w-[480px]"
          >
            {/* Mobile-only brand block */}
            <div className="md:hidden mb-6 text-center">
              <a
                href="/"
                className="inline-flex items-center justify-center gap-2.5"
                aria-label="Mowazaf"
              >
                <img src={Logo} alt="" className="h-9 w-auto select-none" />
                <span className="text-lg font-semibold tracking-tight text-foreground">
                  {t("Mowazaf")}
                </span>
              </a>
              <h1 className="mt-4 text-xl font-semibold tracking-tight text-foreground leading-tight">
                {t("Start free.")}{" "}
                <span className="text-muted-foreground font-normal">
                  {t("Upgrade when you're ready.")}
                </span>
              </h1>
              <div className="mt-2 text-xs text-muted-foreground">
                <a
                  href={LOGIN_URL}
                  className="hover:text-foreground transition-colors underline-offset-2 hover:underline"
                >
                  {t("Already have an account?")}
                </a>
              </div>
            </div>

            {/* Gradient-bordered card. Outer holds the gradient; inner holds
                the surface — 1 px gap creates the border. */}
            <div
              className="rounded-2xl p-px shadow-[0_10px_40px_-15px_rgba(48,78,161,0.25)]"
              style={{
                backgroundImage:
                  "linear-gradient(180deg, rgba(48,78,161,0.45) 0%, rgba(74,221,203,0.18) 35%, var(--color-border) 100%)",
              }}
            >
              <div className="mz-form-card rounded-[15px] bg-card overflow-hidden">
                {/* Progress affordance */}
                <div
                  className="h-[3px] bg-primary/10 relative"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={progress}
                  aria-label={t("Form completion")}
                >
                  <div
                    className={cn(
                      "absolute inset-y-0 start-0 transition-[width] duration-500 ease-out",
                      progress > 0 && progress < 100 && "mz-progress-fill",
                      progress === 100 && "mz-progress-fill is-complete",
                      progress === 0 && "bg-primary",
                    )}
                    style={{ width: `${progress}%` }}
                    aria-hidden
                  />
                </div>

                <div className="px-6 pt-6 pb-2">
                  <div className="hidden md:flex items-center justify-between mb-2">
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium bg-primary/10 text-primary">
                      <Sparkles size={12} className="mz-twinkle" />
                      {t("Free plan")}
                    </span>
                    <a
                      href={LOGIN_URL}
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {t("Already have an account?")}
                    </a>
                  </div>
                  <h2 className="text-xl font-semibold tracking-tight text-foreground">
                    {t("Create your workspace")}
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t("Takes under a minute.")}
                  </p>
                </div>

                <div className="px-6 pb-6 pt-4 space-y-4">
                  <Field
                    label={t("Company name")}
                    required
                    error={touched ? fieldErrors.companyName : undefined}
                  >
                    <Input
                      value={form.companyName}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        update("companyName", e.target.value)
                      }
                      placeholder={t("Acme Inc.")}
                      autoComplete="organization"
                    />
                  </Field>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field
                      label={t("First name")}
                      required
                      error={touched ? fieldErrors.firstName : undefined}
                    >
                      <Input
                        value={form.firstName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          update("firstName", e.target.value)
                        }
                        autoComplete="given-name"
                      />
                    </Field>
                    <Field
                      label={t("Last name")}
                      required
                      error={touched ? fieldErrors.lastName : undefined}
                    >
                      <Input
                        value={form.lastName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          update("lastName", e.target.value)
                        }
                        autoComplete="family-name"
                      />
                    </Field>
                  </div>

                  <Field
                    label={t("Work email")}
                    required
                    error={touched ? fieldErrors.email : undefined}
                  >
                    <div className="relative">
                      <Lock
                        size={14}
                        className="absolute top-1/2 -translate-y-1/2 ltr:left-3 rtl:right-3 text-muted-foreground pointer-events-none"
                        aria-hidden
                      />
                      <Input
                        type="email"
                        value={form.email}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          update("email", e.target.value)
                        }
                        placeholder="you@company.com"
                        autoComplete="email"
                        className="ltr:pl-9 rtl:pr-9"
                      />
                    </div>
                  </Field>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field
                      label={t("Country")}
                      required
                      error={touched ? fieldErrors.countryId : undefined}
                    >
                      <Select
                        searchable
                        value={form.countryId || ""}
                        options={countries.map((c) => ({ value: c.id, label: c.name }))}
                        onChange={(val: number) => update("countryId", val)}
                        placeholder={t("Select a country")}
                      />
                    </Field>
                    <Field label={t("Phone (optional)")}>
                      <Input
                        type="tel"
                        value={form.phoneNumber}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          update("phoneNumber", e.target.value)
                        }
                        autoComplete="tel"
                        placeholder="+971…"
                      />
                    </Field>
                  </div>

                  <Field
                    label={t("Password")}
                    required
                    error={touched ? fieldErrors.password : undefined}
                    hint={t("We generated a strong one for you. Save it somewhere safe.")}
                  >
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={form.password}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          update("password", e.target.value)
                        }
                        className="font-mono ltr:pr-20 rtl:pl-20 tracking-wide"
                        autoComplete="new-password"
                      />
                      <div className="absolute top-1/2 -translate-y-1/2 ltr:right-1 rtl:left-1 flex items-center">
                        <IconBtn
                          onClick={() => setShowPassword((s) => !s)}
                          label={showPassword ? t("Hide password") : t("Show password")}
                        >
                          {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                        </IconBtn>
                        <IconBtn
                          onClick={() => update("password", generatePassword())}
                          label={t("Regenerate password")}
                        >
                          <RefreshCw size={14} />
                        </IconBtn>
                      </div>
                    </div>
                  </Field>

                  <div className="pt-1">
                    <label className="flex items-start gap-2.5 cursor-pointer group">
                      <Checkbox
                        checked={accepted}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setAccepted(e.target.checked)
                        }
                      />
                      <span className="text-xs leading-relaxed text-muted-foreground -mt-0.5">
                        {t("I have read and accept the Mowazaf ")}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            setTermsOpen(true);
                          }}
                          className="text-primary underline underline-offset-2 hover:opacity-80"
                        >
                          {t("Terms of Service")}
                        </button>
                        .
                      </span>
                    </label>
                    {touched && fieldErrors.terms && (
                      <p className="mt-1.5 text-xs text-destructive ps-7">{fieldErrors.terms}</p>
                    )}
                  </div>

                  {serverError && (
                    <div
                      role="alert"
                      className="rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 p-3 text-sm text-red-800 dark:text-red-300"
                    >
                      {serverError}
                    </div>
                  )}

                  <Button
                    onClick={submit}
                    loading={submitting}
                    disabled={!isValid && touched}
                    fullWidth
                    size="lg"
                    className="mz-cta mt-1"
                  >
                    {t("Create my workspace")}
                    <ArrowRight size={16} className="ms-2 rtl:rotate-180" />
                  </Button>

                  <p className="text-[11px] text-center text-muted-foreground">
                    <CheckCircle2
                      size={11}
                      className="inline-block me-1 -mt-0.5 text-emerald-600 dark:text-emerald-400"
                    />
                    {t("Free forever. No card. Upgrade to a 30-day trial when you're ready.")}
                  </p>
                </div>
              </div>
            </div>

            <p className="mt-5 text-center text-xs text-muted-foreground">
              {t("By creating a workspace you agree to our terms and acknowledge our privacy practices.")}
            </p>
          </motion.div>
        </main>
      </div>
    </div>
  );
};

/* ─────────────────────────── Subcomponents ─────────────────────────── */

/** Layered radial-gradient mesh + grain. Fills the parent's `absolute inset-0`.
 *  Two ambient drift layers animate via CSS keyframes (see index.css). The
 *  cobalt + turquoise centres sit inside the left brand pane on desktop; the
 *  form pane's bg-white wash mutes whatever leaks through on the right. */
const BrandMesh: React.FC = () => (
  <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
    {/* Cobalt focus — top-left of brand pane */}
    <div
      className="absolute inset-0 mz-mesh-a"
      style={{
        background:
          "radial-gradient(62% 55% at 22% 26%, rgba(18,58,167,0.55) 0%, rgba(18,58,167,0) 65%)",
      }}
    />
    {/* Turquoise focus — bottom of brand pane */}
    <div
      className="absolute inset-0 mz-mesh-b"
      style={{
        background:
          "radial-gradient(50% 42% at 32% 78%, rgba(74,221,203,0.50) 0%, rgba(74,221,203,0) 70%)",
      }}
    />
    {/* Soft linen warmth — centre, ties the mesh together */}
    <div
      className="absolute inset-0"
      style={{
        background:
          "radial-gradient(70% 55% at 18% 50%, rgba(246,239,234,0.55) 0%, rgba(246,239,234,0) 75%)",
      }}
    />
    {/* Far-edge cobalt glow — leaks slightly into the form pane seam */}
    <div
      className="absolute inset-0"
      style={{
        background:
          "radial-gradient(40% 35% at 6% 90%, rgba(48,78,161,0.32) 0%, rgba(48,78,161,0) 70%)",
      }}
    />
    <div className="absolute inset-0 mz-grain" />
  </div>
);

interface FieldProps {
  label: string;
  required?: boolean;
  error?: string | null;
  hint?: string;
  children: React.ReactNode;
}
const Field: React.FC<FieldProps> = ({ label, required, error, hint, children }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-xs font-medium text-foreground/90">
      {label}
      {required && <span className="text-destructive ms-0.5">*</span>}
    </label>
    <div className="mz-field">{children}</div>
    {error ? (
      <p className="text-xs text-destructive">{error}</p>
    ) : hint ? (
      <p className="text-xs text-muted-foreground/80">{hint}</p>
    ) : null}
  </div>
);

interface FeatureLineProps {
  icon: React.ReactNode;
  title: string;
  body: string;
  variants?: Variants;
}
const FeatureLine: React.FC<FeatureLineProps> = ({ icon, title, body, variants }) => (
  <motion.li variants={variants} className="flex items-start gap-3">
    <span className="shrink-0 w-7 h-7 rounded-md bg-white border border-border text-primary flex items-center justify-center mt-0.5 shadow-[0_1px_2px_rgba(48,78,161,0.06)]">
      {icon}
    </span>
    <div className="min-w-0">
      <div className="text-sm font-medium text-foreground">{title}</div>
      <div className="text-xs text-muted-foreground mt-0.5 max-w-[44ch]">{body}</div>
    </div>
  </motion.li>
);

interface IconBtnProps {
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}
const IconBtn: React.FC<IconBtnProps> = ({ onClick, label, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    title={label}
    className={cn(
      "inline-flex items-center justify-center w-7 h-7 rounded-md",
      "text-muted-foreground hover:bg-muted hover:text-foreground transition-colors",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30",
      "active:scale-95",
    )}
  >
    {children}
  </button>
);

export default SignupFree;
